import React, {
  FC, useMemo, useState, useEffect,
} from 'react';
import _ from 'lodash';
import {
  DropdownItem,
} from 'reactstrap';
import Isvg from 'react-inlinesvg';
import moment from 'moment';
import history from 'app/routes/history';
import Button from 'app/components/general/Button';
import Table from 'app/components/general/Table';
import ActionDots from 'app/components/general/ActionDots';
import StatusBadge from 'app/components/general/StatusBadge';
import BadgeWithTooltip from 'app/components/general/BadgeWithTooltip';
import base64 from 'base-64';

import { IPaymentEvent } from 'app/ducks/paymentEvents/types';
import * as paymentEventsSelectors from 'app/ducks/paymentEvents/selectors';

import { IPaymentCardData } from 'app/ducks/paymentEvents/selectors';
import { superSimpleDate } from 'app/utils/parsers';
import DollarIcon from 'app/images/icons/dollar.svg';

interface IProps {
  actions: any;
  paymentEventsById: IPaymentEvent[];
  paymentEventIds: string[];
  teamPaymentRequests: any;
  teamId?: string;
  headerTitle?: string;
  appReady?: boolean;
  isLoading?: boolean;
  manual?: boolean;
  hideActions?: boolean;
  hideAmount?: boolean;
  showPayMultipleMatchesButton?: boolean;
  aggressiveLoader?: boolean;
  saveFilters?: boolean;
  initialFilterConfig?: any;
  persistentFilterConfig?: any;
  pagination?: any;
  groupIds: {
    teamId: string;
    groupId: string;
  }[];
  groupsById: any;
  selectTeam: Function;
  selectPayment: Function;
  handleClick?: Function;
}

const MatchesTable: FC<IProps> = (props: IProps = {
  manual: true,
  actions: undefined,
  teamPaymentRequests: undefined,
  paymentEventsById: undefined,
  paymentEventIds: undefined,
  initialFilterConfig: undefined,
  persistentFilterConfig: undefined,
  isLoading: false,
  pagination: undefined,
  selectTeam: undefined,
  aggressiveLoader: true,
  showPayMultipleMatchesButton: false,
  selectPaymentEvent: undefined,
}) => {
  const filterOptions = [
    {
      label: 'Team',
      accessor: 'groupName',
      type: 'text',
    },
    {
      label: 'Round',
      accessor: 'roundName',
      type: 'text',
    },
    {
      label: 'Status',
      accessor: 'ignoreStatus',
      type: 'select',
      options: [
        { id: 'pending', name: 'Unpaid' },
        { id: 'paid', name: 'Paid' },
        { id: 'refunded', name: 'Refunded' },
        { id: 'forfeited', name: 'Forfeited' },
        { id: 'all', name: 'All' },
      ],
    },
  ];

  const fetchDataFromApi = ({
    sort, filter, pageNumber, pageSize,
  }): void => {
    const payload = {
      page: {
        size: pageSize,
        number: pageNumber,
      },
      sort,
      filter,
    };
    props.actions.getAllPaymentEventsForAllTeams(payload);
  };

  useEffect(() => {
    if (!_.isEmpty(props.paymentEventIds)) {
      props.actions.getAllTeamPaymentRequests(
        {
          filter: {
            'match-payment-forfeit-id': _.uniq(_.flatMap(props.paymentEventsById, (p) => {
              const returnIds = [];
              const groupIds = _.map(props.groupIds, (g) => g.groupId);
              if (_.includes(groupIds, p.groupOne.id)) {
                returnIds.push(p.groupOneMatchPayment.id);
                returnIds.push(p.groupOneForfeit.id);
              }
              if (_.includes(groupIds, p.groupTwo.id)) {
                returnIds.push(p.groupTwoMatchPayment.id);
                returnIds.push(p.groupTwoForfeit.id);
              }
              return returnIds;
            })).join(','),
          },
          page: {
            size: 200,
          },
        },
        false,
      );
    }
  }, [JSON.stringify(props.paymentEventsById)]);

  const data = useMemo(() => {
    const paymentStatus: string = _.find(props.pagination?.filter, (item) => item.id === 'ignoreStatus')?.value;

    if (_.isEmpty(props.paymentEventsById) || props.paymentEventIds === undefined) {
      return [];
    }
    const rows = _.reduce(
      props.paymentEventIds,
      (acc, id) => {
        const pe = props.paymentEventsById[id];
        if (!_.isEmpty(pe)) {
          const teamGroup1 = _.find(props.groupIds, (t) => t.groupId === pe.groupOne.id);
          const teamGroup2 = _.find(props.groupIds, (t) => t.groupId === pe.groupTwo.id);

          if (teamGroup1 !== undefined) {
            const validGroup1 = paymentStatus === undefined || paymentEventsSelectors.filterPaymentEventByStatus(
              pe,
              teamGroup1?.groupId,
              paymentStatus,
            );

            if (validGroup1) {
              const teamPaymentRequest = _.find(props.teamPaymentRequests?.teamPaymentRequestsById, (p) => (!_.isEmpty(p.matchPayment) && p.matchPayment?.id === pe.groupOneMatchPayment?.id) || (!_.isEmpty(p.forfeit) && p.forfeit?.id === pe.groupOneForfeit?.id));

              acc.push(paymentEventsSelectors.getPaymentEventForMatchCard(
                pe,
                pe.groupOne.id,
                teamGroup1?.teamId,
                teamPaymentRequest,
              ));
            }
          }
          if (teamGroup2 !== undefined) {
            const validGroup2 = paymentStatus === undefined || paymentEventsSelectors.filterPaymentEventByStatus(
              pe,
              teamGroup2?.groupId,
              paymentStatus,
            );

            if (validGroup2) {
              const teamPaymentRequest = _.find(props.teamPaymentRequests?.teamPaymentRequestsById, (p) => (!_.isEmpty(p.matchPayment) && p.matchPayment?.id === pe.groupTwoMatchPayment?.id) || (!_.isEmpty(p.forfeit) && p.forfeit?.id === pe.groupTwoForfeit?.id));
              acc.push(paymentEventsSelectors.getPaymentEventForMatchCard(
                pe,
                pe.groupTwo.id,
                teamGroup2?.teamId,
                teamPaymentRequest,
              ));
            }
          }
        }
        return acc;
      },
      [],
    );

    return rows;
  }, [JSON.stringify(props.paymentEventsById), props.paymentEventIds, props.groupIds, JSON.stringify(props.teamPaymentRequests?.paymentEventsById)]);

  const handleClick = (id: string, row: any): void => {
    if (props.handleClick !== undefined) {
      props.handleClick(id, row);
      return;
    }
    const matchSummary = {
      matchPaymentId: row.matchPaymentId,
      forfeitId: row.forfeitId,
      paymentEnabled: row.paymentEnabled,
      matchStatus: row.matchStatus,
      status: row.status,
      statusBlock: row.statusBlock,
      otherTeamStatus: row.otherTeamStatus,
      otherTeamStatusBlock: row.otherTeamStatusBlock,
      groupId: row.groupId,
      paymentEventId: row.paymentEventId,
      teamId: row.teamId,
    };
    if (matchSummary.paymentEnabled && _.includes(['pending'], matchSummary.status)
      && !_.includes(['walkover'], matchSummary.otherTeamStatusBlock?.forfeitStatus)
      && !_.includes(['cancelled', 'paused'], matchSummary.matchStatus)
      && !_.includes(['no-show'], matchSummary.statusBlock?.adminStatus)
      && !_.includes(['walkover'], matchSummary.otherTeamStatusBlock?.forfeitStatus)
      && !_.includes(['no-show'], matchSummary.otherTeamStatusBlock?.adminStatus)) {
      history.push(
        `/payment/match/${matchSummary.matchPaymentId}/review`,
      );
    } else if (
      _.includes(['paid'], matchSummary.status)
    ) {
      props.selectTeam(matchSummary.teamId);

      history.push(
        `/payment/match/detail/${matchSummary.matchPaymentId}`,
      );
    } else if (
      _.includes(['walkover'], matchSummary.status)
    ) {
      props.selectTeam(matchSummary.teamId);

      history.push(
        `/payment/forfeit/${matchSummary.forfeitId}/receipt`,
      );
    }
  };

  const renderStatus = (cellProps: {
    value: {
      id: string;
      status: string;
      statuses: string[];
    };
  }): React.ReactNode => {
    const targetId = `t-${cellProps.value.id}`;

    return (
      <div className="payment-badge-group">
        {_.map(cellProps.value.statuses, (status) => (
          <BadgeWithTooltip className="payment-badge" targetId={targetId} key={status.text} color={status.colour} tooltipText={status.description}>
            {status.text}
          </BadgeWithTooltip>
          // <StatusBadge
          //   paymentText={status}
          //   tick={false}
          //   className="no-margin-right"
          // />
        ))}
      </div>
    );
  };

  const renderDate = (cellProps: {
    value: string;
  }): string => (_.isEmpty(cellProps.value) ? '' : moment(cellProps.value).format('D MMM YYYY'));

  const renderRound = (cellProps: {
    value: {
      roundName: string;
      roundDate: string;
    };
  }): React.ReactNode => (
    <span className="d-inline">
      {cellProps.value.roundName}
      <span className="margin-left-5 text-weight-medium-bold">
        {` - ${cellProps.value.roundDate}`}
      </span>
    </span>
  );

  const renderDropdownOptions = (pc: {
    matchPaymentId: string;
    forfeitId: string;
    paymentEnabled: boolean;
    status: string;
    statusBlock: string;
    otherTeamStatus: string;
    otherTeamStatusBlock: string;
    groupId: string;
    paymentEventId: string;
    teamId: string;
    round: string;
    amount: string;
    dateTime: string;
    timeRaw: string;
    teamPaymentRequest: any;
  }): React.ReactNode => {
    const payload = {
      description: pc.round,
      amount: pc.amount,
      dueDate: pc.timeRaw,
      matchPaymentId: pc.matchPaymentId,
      groupId: pc.groupId,
      paymentEventId: pc.paymentEventId,
    };
    const payRequestPreconfigData = base64.encode(JSON.stringify(payload));
    const progressPercentage = pc.teamPaymentRequest?.collected === 0
      ? 0
      : (pc.teamPaymentRequest?.collected / pc.teamPaymentRequest?.total) * 100;

    return (
      <div>
        {_.includes(['pending'], pc.statusBlock.paymentStatus)
          && pc.paymentEnabled
          && !_.includes(['walkover'], pc.statusBlock.forfeitStatus)
          && !_.includes(['cancelled', 'paused'], pc.matchStatus)
          && !_.includes(['no-show'], pc.statusBlock.adminStatus)
          && !_.includes(['walkover'], pc.otherTeamStatusBlock.forfeitStatus)
          && !_.includes(['no-show'], pc.otherTeamStatusBlock.adminStatus) && (
          <DropdownItem
            onClick={(): void => {
              props.selectTeam(pc.teamId);

              history.push(
                `/payment/match/${pc.matchPaymentId}/review`,
              );
            }}
          >
            Pay Now
          </DropdownItem>
        )}
        {pc.teamPaymentRequest === undefined && _.includes(
          ['paid', 'paid-cash', 'paid-eftpos', 'pending'],
          pc.status,
        ) && (
          <DropdownItem
            onClick={() => {
              props.selectTeam(pc.teamId);

              history.push(`/payment-requests/new:${payRequestPreconfigData}`);
            }}
          >
            Collect from Team
          </DropdownItem>
        )}
        {pc.teamPaymentRequest !== undefined && (
          <div className="row">
            <div className="w-100 btn-group payment-card-btns">
              <DropdownItem
                className="btn-2 less-padding no-margin-right"
                onClick={() => history.push(`/payment-requests/detail/${pc.teamPaymentRequest.id}`)}
              >
                {progressPercentage === 100 ? 'View Request' : 'Track Request'}
              </DropdownItem>
            </div>
          </div>
        )}
        {_.includes(
          ['pending', 'paid', 'paid-cash', 'paid-eftpos', 'paid-transferred', 'paid-refunded', 'paid-prepaid'],
          pc.statusBlock?.paymentStatus,
        )
          && !_.includes(['walkover'], pc.statusBlock?.forfeitStatus)
          && !_.includes(['walkover'], pc.otherTeamStatusBlock?.forfeitStatus)
          && !_.includes(['no-show'], pc.otherTeamStatusBlock?.adminStatus) && !_.includes(['cancelled', 'paused'], pc.matchStatus) && (
          <DropdownItem
            onClick={(): void => {
              props.selectTeam(pc.teamId);
              history.push(
                `/forfeit/${pc.paymentEventId}/${pc.groupId}/submit`,
              );
            }}
          >
            Submit Forfeit
          </DropdownItem>
        )}
        {_.includes(['paid', 'walkover'], pc.status) && !_.includes(['walkover'], pc.statusBlock?.forfeitStatus) && (
          <DropdownItem
            onClick={() => {
              props.selectTeam(pc.teamId);

              history.push(
                `/payment/match/detail/${pc.matchPaymentId}`,
              );
            }}
          >
            View Payment
          </DropdownItem>
        )}
        {_.includes(['walkover'], pc.statusBlock?.forfeitStatus) && (
          <DropdownItem
            onClick={() => {
              props.selectTeam(pc.teamId);

              history.push(
                `/payment/forfeit/${pc.forfeitId}/receipt`,
              );
            }}
          >
            View Forfeit
          </DropdownItem>
        )}
      </div>
    );
  };

  const renderActionDots = (cellProps: {
    value: {
      matchPaymentId: string;
      forfeitId: string;
      paymentEnabled: boolean;
      status: string;
      statusBlock: any;
      otherTeamStatus: string;
      otherTeamStatusBlock: any;
      groupId: string;
      paymentEventId: string;
      teamId: string;
      round: string;
      amount: string;
      dateTime: string;
      timeRaw: string;
      teamPaymentRequest: any;
    };
  }): React.ReactNode => (
    <ActionDots id={cellProps?.value?.matchPaymentId}>
      {renderDropdownOptions({
        matchPaymentId: cellProps.value.matchPaymentId,
        forfeitId: cellProps.value.forfeitId,
        paymentEnabled: cellProps.value.paymentEnabled,
        status: cellProps.value.status,
        statusBlock: cellProps.value.statusBlock,
        otherTeamStatus: cellProps.value.otherTeamStatus,
        otherTeamStatusBlock: cellProps.value.otherTeamStatusBlock,
        groupId: cellProps.value.groupId,
        paymentEventId: cellProps.value.paymentEventId,
        teamId: cellProps.value.teamId,
        round: cellProps.value.round,
        amount: cellProps.value.amount,
        dateTime: cellProps.value.dateTime,
        timeRaw: cellProps.value.timeRaw,
        teamPaymentRequest: cellProps.value.teamPaymentRequest,
      })}
    </ActionDots>
  );

  const tinyColumn = 'col d-none d-lg-flex col-3 col-md-2 col-lg-1';
  const smallColumn = 'col col-3 col-lg-2';
  const growColumn = 'col d-flex';
  const mediumColumn = 'col col-4 col-md-3 col-lg-3 col-xl-2';
  const largeColumn = 'col col-4 col-md-4 col-lg-4 col-xl-3';

  const columns = useMemo(() => {
    const columnsParsed = [
      {
        id: 'groupName',
        sortable: false,
        Header: 'Team',
        // accessor: (row) => ({ id: row.groupId, name: props.groupsById[row.groupId]?.name }),
        // Cell: (cellProps: any) => cellProps.value.name,
        accessor: 'teamName',
        className: growColumn,
        headerClassName: growColumn,
      },
      {
        Header: 'Round',
        id: 'roundName',
        accessor: (row) => ({ roundName: row.round, roundDate: row.dateTime }),
        className: mediumColumn,
        headerClassName: mediumColumn,
        Cell: renderRound,
      },
      {
        id: 'statusCol',
        sortable: false,
        Header: 'Status',
        className: largeColumn,
        isVisble: !props.hideActions,
        headerClassName: largeColumn,
        accessor: (row) => ({
          id: row.paymentEventId,
          status: row.status,
          statuses: row.statuses,
          matchPaymentId: row.matchPaymentId,
        }),
        Cell: renderStatus,
      },
    ];
    if (props.hideAmount !== true) {
      columnsParsed.push({
        Header: 'Amount',
        sortable: false,
        accessor: 'amount',
        className: smallColumn,
        headerClassName: smallColumn,
      });
    }
    if (props.hideActions !== true) {
      columnsParsed.push(
        {
          id: 'actionCol',
          Header: 'Actions',
          className: smallColumn,
          headerClassName: smallColumn,
          accessor: (row: IPaymentCardData) => ({
            matchPaymentId: row.matchPaymentId,
            forfeitId: row.forfeitId,
            paymentEnabled: row.paymentEnabled,
            status: row.status,
            statusBlock: row.statusBlock,
            otherTeamStatus: row.otherTeamStatus,
            otherTeamStatusBlock: row.otherTeamStatusBlock,
            groupId: row.groupId,
            paymentEventId: row.paymentEventId,
            teamId: row.teamId,
            round: row.round,
            amount: row.amount,
            dueDate: row.dueDate,
            timeRaw: row.timeRaw,
            teamPaymentRequest: row.teamPaymentRequest,
          }),
          Cell: renderActionDots,
        },
      );
    }
    return columnsParsed;
  }, []);

  const counterText = props.pagination.meta.totalCount === 1 ? '1 match' : `${props.pagination.meta.totalCount || 0} matches`;

  return (
    <div>
      <Table
        data={data}
        columns={columns}
        isLoading={props.isLoading}
        aggressiveLoader={props.aggressiveLoader}
        headerTitle={props.headerTitle}
        objectTitle="matches"
        filterTitle="Matches Data"
        counterText={counterText}
        handleClick={handleClick}
        filterable
        saveFilters={props.saveFilters}
        manual={props.manual}
        fetchDataFromApi={fetchDataFromApi}
        filterOptions={filterOptions}
        filterConfig={props.initialFilterConfig}
        persistentFilterConfig={props.persistentFilterConfig}
        pagination={props.pagination}
        showPagination
      >
        {props.showPayMultipleMatchesButton && (
          <Button onClick={() => { history.push('/matches/pay-multiple'); }} noLoading className="btn-thinner btn-2">
            <Isvg src={DollarIcon} className="btn-icon" />
            Pay Multiple
          </Button>
        )}
      </Table>
    </div>
  );
};

export default MatchesTable;
